import React from "react";
import { Laptop, Moon, Sun } from "lucide-react";
import { useTheme } from "@/shared/hooks/useTheme";
import { Tooltip } from "@heroui/react";

const THEME_OPTIONS = [
  { id: "light", label: "Light", icon: Sun },
  { id: "system", label: "System", icon: Laptop },
  { id: "dark", label: "Dark", icon: Moon },
];

export default function ThemeToggle({ compact = false, className = "" }) {
  const { theme, setTheme } = useTheme();

  if (compact) {
    const currentOpt =
      THEME_OPTIONS.find((opt) => opt.id === theme) || THEME_OPTIONS[1];
    const Icon = currentOpt.icon;
    const nextThemeMap = { light: "dark", dark: "system", system: "light" };

    return (
      <Tooltip
        content={`Theme: ${currentOpt.label} (Click to switch)`}
        placement="right"
        offset={12}
        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium text-xs shadow-md border border-gray-100 dark:border-gray-700 px-3 py-1.5 rounded-lg"
      >
        <button
          type="button"
          onClick={() => setTheme(nextThemeMap[theme] || "light")}
          aria-label={`Current theme: ${currentOpt.label}. Click to switch.`}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200/80 bg-white dark:border-gray-800 dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:text-secondary dark:hover:text-secondary-light hover:border-secondary/30 shadow-2xs transition-all duration-150 active:scale-95 cursor-pointer shrink-0"
        >
          <Icon size={15} strokeWidth={1.8} />
        </button>
      </Tooltip>
    );
  }

  return (
    <div
      className={`flex flex-col gap-1.5 w-full ${className}`}
      role="group"
      aria-label="Theme mode selector"
    >
      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-1 dark:text-gray-400">
        Theme
      </span>
      <div className="flex w-full min-w-0 items-center rounded-lg border border-gray-200 bg-gray-100/70 p-0.5 dark:border-gray-800 dark:bg-gray-900/80 shadow-xs">
        {THEME_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const isSelected = theme === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setTheme(opt.id)}
              title={`${opt.label} mode`}
              className={`flex min-w-0 flex-auto items-center justify-center gap-[3px] rounded-md py-1.5 px-[3px] text-[11px] font-medium transition-all duration-150 ${
                isSelected
                  ? "bg-white text-secondary shadow-xs font-semibold dark:bg-gray-800 dark:text-secondary-light"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              <Icon size={12} className="shrink-0" />
              <span className="truncate">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
